/**
 * /c/:attemptId — Challenge viewer.
 *
 * Plan B-V2 viral artifact. The voyeur loop: someone shares their kick,
 * a stranger lands here, watches the trajectory, sees the score + flag +
 * percentile, hits "Take your shot" — and is now playing today's puzzle.
 *
 * Spoiler-safe: we render the friend's trajectory and the goal/wall, but
 * the keeper stays at rest position. The viewer's own attempt will reveal
 * the keeper's actual dive when they submit at /play.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { PitchRenderer } from "@/game/render";
import { attachCanvas } from "@/game/canvas";
import type { Outcome, PublicPuzzleParams, Vec3 } from "@/game";

interface FriendData {
  attemptId: string;
  friend: {
    displayName: string;
    countryCode: string;
    score: number;
    outcome: Outcome;
    outcomeDetail: string | null;
    shareCardText: string | null;
  };
  puzzle: {
    id: string;
    date: string;
    params: PublicPuzzleParams;
    stadiumName: string | null;
    conditionsLabel: string | null;
  };
  input: { angle: number; power: number; curl: number };
  trajectoryData: Vec3[] | null;
  isToday: boolean;
  percentile: number | null;
  totalPlayers: number;
}

function flagFor(country: string): string {
  if (!country || country === "XX" || country.length !== 2) return "🌐";
  const A = 0x1f1e6;
  const codePoints = country
    .toUpperCase()
    .split("")
    .map((c) => A + c.charCodeAt(0) - "A".charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function describeOutcome(o: Outcome, detail: string | null): string {
  switch (o) {
    case "GOAL":
      return detail ? `GOAL · ${detail.replace(/_/g, " ")}` : "GOAL";
    case "SAVED":
      return "SAVED";
    case "POST":
      return "OFF THE POST";
    case "WALL":
      return "BLOCKED BY WALL";
    case "OVER":
      return "OVER THE BAR";
    case "WIDE":
      return "WIDE";
    default:
      return String(o);
  }
}

export function ChallengePage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [data, setData] = useState<FriendData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch the replay payload.
  useEffect(() => {
    if (!attemptId) return;
    let cancelled = false;
    api
      .attemptReplay(attemptId)
      .then((d) => {
        if (cancelled) return;
        setData(d as FriendData);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setError("This challenge link isn't valid (or the player was removed).");
        } else {
          setError("Couldn't load this challenge. Try refreshing.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attemptId]);

  // Render the friend's trajectory once data + canvas are ready.
  useEffect(() => {
    if (!data || !canvasRef.current) return;
    const view = attachCanvas(canvasRef.current);
    const renderer = new PitchRenderer(view, data.puzzle.params);
    const trajectory = data.trajectoryData ?? [];
    if (!trajectory.length) {
      // No trajectory available — render a static frame.
      renderer.draw({
        ball: { x: 0, y: 0.11, z: 0 },
        replayTime: 0,
        phase: "AIMING",
        aim: null,
        preview: null,
        keeperReveal: null,
      });
      return () => view.dispose();
    }

    // Animate the friend's trajectory over ~2.6 seconds.
    const TOTAL_SEC = 2.6;
    const PIN_HOLD_SEC = 1.2; // hold the final frame so the result lands
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = (now - start) / 1000;
      const t = Math.min(elapsed, TOTAL_SEC);
      // Sample trajectory: trajectoryData is sampled at ~100 ms intervals
      // server-side (50 frames over ~5 s), so we map elapsed → idx.
      const samples = trajectory.length;
      const idx = Math.min(samples - 1, Math.floor((t / TOTAL_SEC) * samples));
      const ball = trajectory[idx] ?? trajectory[samples - 1]!;
      renderer.draw({
        ball,
        replayTime: t,
        phase: "REPLAYING",
        aim: null,
        preview: null,
        keeperReveal: null, // keeper stays at rest — spoiler-safe
      });
      if (elapsed < TOTAL_SEC + PIN_HOLD_SEC) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      view.dispose();
    };
  }, [data]);

  if (error) {
    return (
      <main className="flex min-h-full flex-col items-center justify-center gap-4 p-6 text-center text-white">
        <p className="opacity-80">{error}</p>
        <Link to="/play" className="rounded-full bg-white/10 px-6 py-2 hover:bg-white/20">
          Take today&rsquo;s kick
        </Link>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="flex min-h-full flex-col items-center justify-center gap-4 p-6 text-white">
        <p className="opacity-60">Loading challenge…</p>
      </main>
    );
  }

  const f = data.friend;
  const flag = flagFor(f.countryCode);
  const isGoal = f.outcome === "GOAL";

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col gap-4 p-4 text-white">
      <header className="text-center">
        <Link
          to="/"
          className="text-xs uppercase tracking-widest opacity-50 hover:opacity-100"
        >
          ← Curvle
        </Link>
        <h1 className="mt-3 text-xl font-semibold">
          <span aria-hidden="true">{flag}</span>{" "}
          <span className="text-amber-200">{f.displayName}</span>{" "}
          challenges you
        </h1>
        {data.puzzle.stadiumName && (
          <p className="mt-1 text-xs opacity-60">
            {data.puzzle.stadiumName}
            {data.puzzle.conditionsLabel ? ` · ${data.puzzle.conditionsLabel}` : ""}
          </p>
        )}
      </header>

      {/* Replay canvas */}
      <div className="aspect-square w-full overflow-hidden rounded-3xl bg-emerald-950/60 ring-1 ring-white/10">
        <canvas ref={canvasRef} className="h-full w-full" />
      </div>

      {/* Stats card */}
      <div
        className={`rounded-2xl p-4 ring-1 ${
          isGoal
            ? "bg-amber-300/10 ring-amber-300/40"
            : "bg-white/5 ring-white/10"
        }`}
      >
        <p className="text-xs uppercase tracking-widest opacity-60">
          {describeOutcome(f.outcome, f.outcomeDetail)}
        </p>
        <div className="mt-1 flex items-baseline gap-3">
          <span className="text-3xl font-bold tabular-nums">
            {isGoal ? f.score : "—"}
          </span>
          <span className="text-sm opacity-70">
            {isGoal ? "/ 1000" : "no goal"}
          </span>
        </div>
        {data.percentile !== null && data.totalPlayers > 0 && (
          <p className="mt-2 text-xs opacity-70">
            Top <strong>{data.percentile}%</strong> of {data.totalPlayers}{" "}
            players today
          </p>
        )}
      </div>

      {/* CTA */}
      <button
        type="button"
        className="w-full rounded-full bg-amber-300 px-6 py-3 text-base font-semibold text-emerald-950 transition hover:bg-amber-200"
        onClick={() => navigate("/play")}
      >
        {data.isToday ? "Take your shot →" : "Play today's kick →"}
      </button>
      {!data.isToday && (
        <p className="text-center text-xs opacity-60">
          This challenge is from {data.puzzle.date}. Today&rsquo;s puzzle is
          fresh — same idea, new keeper.
        </p>
      )}

      <p className="text-center text-[10px] opacity-40">
        Curvle &mdash; one free kick a day, one chance.
      </p>
    </main>
  );
}
